import "./index.css";
import { useState, useMemo, useEffect } from "react";
import { Analytics } from "@vercel/analytics/react";
import { BrowserRouter, Routes, Route, useParams, useNavigate, Link } from "react-router-dom";
import { BrandCard } from "./components/BrandCard";
import { BrandDetail } from "./components/BrandDetail";
import { BrandDetailSkeleton } from "./components/BrandDetailSkeleton";
import { FilterBar } from "./components/FilterBar";
import { ScrollToTop } from "./components/ScrollToTop";
import { Comparador } from "./components/Comparador";
import { WelcomePopup } from "./components/WelcomePopup";
import { LicensePlates } from "./components/LicensePlates";
import { LastLicensePlate } from "./components/LastLicensePlate";
import { CalculateLicensePlate } from "./components/CalculateLicensePlate";
import { Header } from "./components/Header";
import { Grups } from "./components/Grups";
import { PorscheCursor } from "./components/PorscheCursor";
import { CarList } from "./components/CarList";
import { Gallery } from "./components/Gallery";
import { Novetats } from "./components/Novetats";
import { Dades } from "./components/dades/Dades";
import { Histories } from "./components/Histories";
import { HistoriaPorsche } from "./components/HistoriaPorsche";
import { NotFound } from "./components/NotFound";
import { GuiaModels } from "./components/GuiaModels";
import { TestModels } from "./components/TestModels";
import { TestSelector } from "./components/TestSelector";
import { TestDecodifica } from "./components/TestDecodifica";
import { TestPreu } from "./components/TestPreu";
import { carBrands } from "./data/carBrands";
import { findModelImage, getHighResImage, isSimilarModelImage } from "./utils/imageMatching";
import { usePageMeta } from "./utils/usePageMeta";

const getFuelCategory = (fuel: string): string => {
  const fuelLower = fuel.toLowerCase();
  if (fuelLower.includes("elèctric") && !fuelLower.includes("híbrid") && !fuelLower.includes("phev"))
    return "Elèctric";
  if (
    fuelLower.includes("phev") ||
    fuelLower.includes("híbrid") ||
    fuelLower.includes("mhev") ||
    fuelLower.includes("hev")
  )
    return "Híbrid";
  if (fuelLower.includes("gasolina") && fuelLower.includes("dièsel")) return "Gasolina / Dièsel";
  if (fuelLower.includes("gasolina")) return "Gasolina";
  if (fuelLower.includes("dièsel") || fuelLower.includes("diesel")) return "Dièsel";
  return fuel;
};

const allFuelTypes: string[] = (() => {
  const categories = new Set<string>();
  carBrands.forEach(brand =>
    brand.models.forEach(model => {
      if (model.fuel) categories.add(getFuelCategory(model.fuel));
    })
  );
  const fuelOrder = ["Elèctric", "Híbrid", "Gasolina", "Dièsel", "Gasolina / Dièsel"];
  return Array.from(categories).sort((a, b) => {
    const ia = fuelOrder.indexOf(a);
    const ib = fuelOrder.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });
})();

const BrandDetailPage = () => {
  const { brandId } = useParams<{ brandId: string }>();
  const navigate = useNavigate();

  const brand = carBrands.find((b) => b.id === brandId);

  // Track which brandId has finished loading to know when to show skeleton
  const [readyBrandId, setReadyBrandId] = useState<string | undefined>(undefined);

  usePageMeta(
    brand
      ? {
          title: `${brand.name} | Cotx.es`,
          description: `Tots els models de ${brand.name}: preus, potències, maleter i dimensions. ${brand.models.length} models disponibles.`,
        }
      : {
          title: "Marca no trobada | Cotx.es",
          description: "Aquesta marca no existeix al directori de Cotx.es.",
        }
  );

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [brandId]);

  // Show skeleton on every brand navigation; reveal content after two animation frames
  // so the browser has time to paint the skeleton before the heavy render begins
  useEffect(() => {
    let raf1: number, raf2: number;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setReadyBrandId(brandId));
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
      setReadyBrandId(undefined);
    };
  }, [brandId]);

  if (!brand) {
    return <NotFound />;
  }

  const isLoading = readyBrandId !== brandId;

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-7xl">
          {isLoading ? (
            <BrandDetailSkeleton modelCount={Math.min(brand.models.length, 9)} />
          ) : (
            <BrandDetail key={brand.id} brand={brand} />
          )}
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

const BrandCardSkeleton = () => (
  <div className="bg-card border border-border rounded-2xl p-5 animate-pulse">
    <div className="flex flex-col items-center gap-3">
      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-secondary rounded-xl" />
      <div className="w-full text-center space-y-2">
        <div className="h-4 bg-secondary rounded w-3/4 mx-auto" />
        <div className="h-5 bg-secondary rounded-full w-10 mx-auto" />
      </div>
    </div>
  </div>
);

const HomePage = () => {
  usePageMeta({
    title: "Directori de cotxes | Cotx.es",
    description:
      "Directori de marques i models de cotxes. Cerca fàcilment per marca, model, combustible, preu i dimensions.",
  });

  // Show skeleton grid on first mount while brand cards render
  const [isHomeReady, setIsHomeReady] = useState(false);
  useEffect(() => {
    let raf1: number, raf2: number;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => setIsHomeReady(true));
    });
    return () => { cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, []);

  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<"name" | "models">("name");
  const [activeFuels, setActiveFuels] = useState<string[]>([]);
  const [minPower, setMinPower] = useState("");
  const [maxPower, setMaxPower] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minTrunk, setMinTrunk] = useState("");
  const [maxTrunk, setMaxTrunk] = useState("");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [recentSearches, setRecentSearches] = useState<string[]>(() => {
    try {
      return JSON.parse(localStorage.getItem("cotx-recent-searches") || "[]");
    } catch {
      return [];
    }
  });

  const hasAdvancedFilters =
    !!minPower || !!maxPower || !!minPrice || !!maxPrice || !!minTrunk || !!maxTrunk;
  const hasActiveFilters = activeFuels.length > 0 || hasAdvancedFilters;

  // Save recent searches with debounce
  useEffect(() => {
    if (!searchQuery.trim() || searchQuery.trim().length < 3) return;
    const timer = setTimeout(() => {
      setRecentSearches(prev => {
        const updated = [
          searchQuery.trim(),
          ...prev.filter(s => s !== searchQuery.trim()),
        ].slice(0, 5);
        localStorage.setItem("cotx-recent-searches", JSON.stringify(updated));
        return updated;
      });
    }, 1000);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const carwowAvailableBrands = [
    "Alpine", "Audi", "BMW", "BYD", "Citroën", "Cupra", "Dacia", "Dongfeng",
    "EBRO", "Fiat", "Ford", "Honda", "Hyundai", "JAECOO", "Jeep", "KIA",
    "Leapmotor", "Lexus", "Mazda", "Mercedes", "Mercedes Benz", "MG", "MINI", "Nissan",
    "OMODA", "Opel", "Peugeot", "Polestar", "Renault", "SEAT", "Skoda",
    "smart", "Suzuki", "Tesla", "Toyota", "Volkswagen", "Volvo", "XPENG",
  ];

  const generateReviewUrl = (brandName: string, modelName: string): string => {
    const isReviewAvailable = carwowAvailableBrands.some(
      availableBrand => availableBrand.toLowerCase() === brandName.toLowerCase()
    );
    if (isReviewAvailable) {
      return `https://www.google.com/search?q=${encodeURIComponent(`${brandName} ${modelName} site:carwow.es`)}`;
    }
    return `https://www.google.com/search?q=${encodeURIComponent(`${brandName} ${modelName} "review"`)}`;
  };

  const handleImageClick = (brandName: string, modelName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(
      `https://www.google.com/search?q=${encodeURIComponent(`${brandName} ${modelName}`)}&tbm=isch`,
      "_blank"
    );
  };

  const handleReviewClick = (brandName: string, modelName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.open(generateReviewUrl(brandName, modelName), "_blank");
  };

  const handleFuelToggle = (fuel: string) => {
    setActiveFuels(prev =>
      prev.includes(fuel) ? prev.filter(f => f !== fuel) : [...prev, fuel]
    );
  };

  const handleResetFilters = () => {
    setActiveFuels([]);
    setMinPower("");
    setMaxPower("");
    setMinPrice("");
    setMaxPrice("");
    setMinTrunk("");
    setMaxTrunk("");
  };

  const searchResults = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const hasFilters =
      activeFuels.length > 0 ||
      !!minPower || !!maxPower || !!minPrice || !!maxPrice || !!minTrunk || !!maxTrunk;

    if (!query && !hasFilters) {
      return { type: "brands" as const, data: [] };
    }

    if (hasFilters) {
      const matchingModels: Array<{ brand: string; brandId: string; model: any }> = [];
      carBrands.forEach(brand => {
        brand.models.forEach(model => {
          let matches = true;

          if (query) {
            const brandMatch = brand.name.toLowerCase().includes(query);
            const modelMatch = model.name.toLowerCase().includes(query);
            if (!brandMatch && !modelMatch) matches = false;
          }

          if (matches && activeFuels.length > 0) {
            if (!activeFuels.includes(getFuelCategory(model.fuel || ""))) matches = false;
          }

          if (matches && (minPower || maxPower)) {
            const power = parseInt((model.power?.split("/")[0] || "").replace(/\D/g, "")) || 0;
            if (minPower && power < parseInt(minPower)) matches = false;
            if (maxPower && power > parseInt(maxPower)) matches = false;
          }

          if (matches && (minPrice || maxPrice)) {
            const price = parseInt((model.price || "").replace(/[^\d]/g, "")) || 0;
            if (minPrice && price < parseInt(minPrice)) matches = false;
            if (maxPrice && price > parseInt(maxPrice)) matches = false;
          }

          if (matches && (minTrunk || maxTrunk)) {
            const trunk = parseInt(model.trunk || "") || 0;
            if (minTrunk && trunk < parseInt(minTrunk)) matches = false;
            if (maxTrunk && trunk > parseInt(maxTrunk)) matches = false;
          }

          if (matches) matchingModels.push({ brand: brand.name, brandId: brand.id, model });
        });
      });
      return { type: "models" as const, data: matchingModels };
    }

    // Text-only search
    const matchingModels: Array<{ brand: string; brandId: string; model: any }> = [];
    const matchingBrands = carBrands.filter(brand => {
      const brandMatches = brand.name.toLowerCase().includes(query);
      brand.models.forEach(model => {
        if (model.name.toLowerCase().includes(query)) {
          matchingModels.push({ brand: brand.name, brandId: brand.id, model });
        }
      });
      return brandMatches;
    });

    const hasExactBrandMatch = matchingBrands.some(b => b.name.toLowerCase() === query);
    if (matchingModels.length > 0 && !hasExactBrandMatch && matchingBrands.length <= 2) {
      return { type: "models" as const, data: matchingModels };
    }
    return { type: "brands" as const, data: matchingBrands };
  }, [searchQuery, activeFuels, minPower, maxPower, minPrice, maxPrice, minTrunk, maxTrunk]);

  const filteredAndSortedBrands = useMemo(() => {
    const hasFilters =
      activeFuels.length > 0 ||
      !!minPower || !!maxPower || !!minPrice || !!maxPrice || !!minTrunk || !!maxTrunk;

    if (!searchQuery.trim() && !hasFilters) {
      const sorted = [...carBrands];
      if (sortBy === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
      else sorted.sort((a, b) => b.models.length - a.models.length);
      return sorted;
    }

    if (searchResults.type === "brands") {
      const sorted = [...searchResults.data];
      if (sortBy === "name") sorted.sort((a, b) => a.name.localeCompare(b.name));
      else sorted.sort((a, b) => b.models.length - a.models.length);
      return sorted;
    }

    return [];
  }, [searchResults, sortBy, searchQuery, activeFuels, minPower, maxPower, minPrice, maxPrice, minTrunk, maxTrunk]);

  const totalModels = useMemo(
    () => carBrands.reduce((sum, brand) => sum + brand.models.length, 0),
    []
  );
  const totalDiscontinuedModels = useMemo(
    () => carBrands.reduce((sum, brand) => sum + (brand.discontinuedModels?.length || 0), 0),
    []
  );

  useEffect(() => {
    if (searchQuery) setSortBy("name");
  }, [searchQuery]);

  const isModelView = searchResults.type === "models";

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          {/* Avís Novetats */}
          <div className="mb-5 flex justify-center">
            <Link
              to="/novetats"
              className="group inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-card pl-1 pr-3 py-1 text-sm shadow-sm hover:border-foreground transition-colors"
            >
              <span className="shrink-0 rounded-full bg-foreground text-background px-2.5 py-0.5 text-xs font-semibold">
                Novetats
              </span>
              <span className="truncate text-muted-foreground group-hover:text-foreground transition-colors">
                Porsche Mission S<span className="hidden sm:inline"> i el Saló de París</span><span className="sm:hidden"> i més</span>
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 text-muted-foreground group-hover:text-foreground group-hover:translate-x-0.5 transition-all" aria-hidden="true">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            </Link>
          </div>

          <div className="mb-8 text-center">
            <h1 className="text-3xl sm:text-4xl font-bold mb-2 text-foreground">
              Catàleg de marques i models de cotxes
            </h1>
          </div>

          {/* Unified search + filter card */}
          <div className="w-full max-w-2xl mx-auto mb-10">
            <div className="bg-card border-2 border-border rounded-2xl overflow-hidden shadow-sm">

              {/* Search input row */}
              <div className="relative flex items-center">
                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="Cerca per marca o model..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  aria-label="Cercar marca o model de cotxe"
                  className="w-full pl-12 pr-12 py-4 text-base bg-transparent text-foreground focus:outline-none placeholder:text-muted-foreground"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    type="button"
                    aria-label="Esborrar cerca"
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <line x1="15" y1="9" x2="9" y2="15" />
                      <line x1="9" y1="9" x2="15" y2="15" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Filters row */}
              {(() => {
                const pillClass = (fuel: string) =>
                  `px-2.5 py-1 text-xs rounded-lg transition-all font-medium ${
                    activeFuels.includes(fuel)
                      ? "bg-foreground text-background"
                      : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
                  }`;

                const actionButtons = (
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                      type="button"
                      aria-label="Mostrar filtres avançats"
                      aria-expanded={showAdvancedFilters}
                      className={`px-2.5 py-1 text-xs rounded-lg transition-all font-medium flex items-center gap-1 ${
                        hasAdvancedFilters
                          ? "bg-foreground text-background"
                          : "bg-secondary text-secondary-foreground hover:bg-secondary/70"
                      }`}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="4" y1="21" x2="4" y2="14" /><line x1="4" y1="10" x2="4" y2="3" />
                        <line x1="12" y1="21" x2="12" y2="12" /><line x1="12" y1="8" x2="12" y2="3" />
                        <line x1="20" y1="21" x2="20" y2="16" /><line x1="20" y1="12" x2="20" y2="3" />
                        <line x1="1" y1="14" x2="7" y2="14" /><line x1="9" y1="8" x2="15" y2="8" /><line x1="17" y1="16" x2="23" y2="16" />
                      </svg>
                      Filtres
                    </button>
                    {hasActiveFilters && (
                      <button
                        onClick={handleResetFilters}
                        type="button"
                        aria-label="Netejar tots els filtres"
                        className="px-2.5 py-1 text-xs rounded-lg bg-secondary text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors font-medium flex items-center gap-1"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                        Netejar
                      </button>
                    )}
                  </div>
                );

                return (
                  <div className="border-t border-border px-3 py-2 space-y-1.5 sm:space-y-0">
                    {/* Mobile: row 1 — first 3 pills + action buttons */}
                    <div className="flex items-center gap-1.5 sm:hidden">
                      {allFuelTypes.slice(0, 3).map(fuel => (
                        <button key={fuel} onClick={() => handleFuelToggle(fuel)} type="button"
                          aria-label={`Filtrar per ${fuel}`} aria-pressed={activeFuels.includes(fuel)}
                          className={pillClass(fuel)}
                        >{fuel}</button>
                      ))}
                      <div className="ml-auto">{actionButtons}</div>
                    </div>
                    {/* Mobile: row 2 — remaining pills (Dièsel, GLP…) */}
                    {allFuelTypes.length > 3 && (
                      <div className="flex items-center gap-1.5 sm:hidden">
                        {allFuelTypes.slice(3).map(fuel => (
                          <button key={fuel} onClick={() => handleFuelToggle(fuel)} type="button"
                            aria-label={`Filtrar per ${fuel}`} aria-pressed={activeFuels.includes(fuel)}
                            className={pillClass(fuel)}
                          >{fuel}</button>
                        ))}
                      </div>
                    )}
                    {/* Desktop: single row */}
                    <div className="hidden sm:flex items-center justify-between gap-2">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {allFuelTypes.map(fuel => (
                          <button key={fuel} onClick={() => handleFuelToggle(fuel)} type="button"
                            aria-label={`Filtrar per ${fuel}`} aria-pressed={activeFuels.includes(fuel)}
                            className={pillClass(fuel)}
                          >{fuel}</button>
                        ))}
                      </div>
                      {actionButtons}
                    </div>
                  </div>
                );
              })()}

              {/* Advanced filter panel */}
              {showAdvancedFilters && (
                <div className="border-t border-border px-4 py-3">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                        </svg>
                        Potència (CV)
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          placeholder="Mín."
                          value={minPower}
                          onChange={e => setMinPower(e.target.value)}
                          aria-label="Potència mínima en CV"
                          className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-foreground text-xs focus:outline-none focus:border-ring transition-colors"
                        />
                        <input
                          type="number"
                          placeholder="Màx."
                          value={maxPower}
                          onChange={e => setMaxPower(e.target.value)}
                          aria-label="Potència màxima en CV"
                          className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-foreground text-xs focus:outline-none focus:border-ring transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <line x1="12" y1="2" x2="12" y2="22" />
                          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                        </svg>
                        Preu (€)
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          placeholder="Mín."
                          value={minPrice}
                          onChange={e => setMinPrice(e.target.value)}
                          aria-label="Preu mínim en euros"
                          className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-foreground text-xs focus:outline-none focus:border-ring transition-colors"
                        />
                        <input
                          type="number"
                          placeholder="Màx."
                          value={maxPrice}
                          onChange={e => setMaxPrice(e.target.value)}
                          aria-label="Preu màxim en euros"
                          className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-foreground text-xs focus:outline-none focus:border-ring transition-colors"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-foreground mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="12"
                          height="12"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <rect x="3" y="8" width="18" height="4" rx="1" />
                          <path d="M12 8v13" />
                          <path d="M19 12v7a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2v-7" />
                        </svg>
                        Maleter (L)
                      </label>
                      <div className="flex gap-1.5">
                        <input
                          type="number"
                          placeholder="Mín."
                          value={minTrunk}
                          onChange={e => setMinTrunk(e.target.value)}
                          aria-label="Capacitat mínima del maleter en litres"
                          className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-foreground text-xs focus:outline-none focus:border-ring transition-colors"
                        />
                        <input
                          type="number"
                          placeholder="Màx."
                          value={maxTrunk}
                          onChange={e => setMaxTrunk(e.target.value)}
                          aria-label="Capacitat màxima del maleter en litres"
                          className="w-full px-2.5 py-1.5 border border-border rounded-lg bg-background text-foreground text-xs focus:outline-none focus:border-ring transition-colors"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Recent searches — below card */}
            {!searchQuery && recentSearches.length > 0 && (
              <div className="mt-3 flex items-center gap-2 flex-wrap">
                <span className="text-xs text-muted-foreground shrink-0">Recents:</span>
                {recentSearches.map((term, i) => (
                  <button
                    key={i}
                    onClick={() => setSearchQuery(term)}
                    type="button"
                    tabIndex={0}
                    aria-label={`Cercar "${term}" de nou`}
                    className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-secondary text-secondary-foreground rounded-full hover:bg-secondary/70 transition-colors ${i >= 3 ? "hidden sm:inline-flex" : ""}`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="11"
                      height="11"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <polyline points="12 6 12 12 16 14" />
                    </svg>
                    {term}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* FilterBar — only in brand grid mode */}
          {!isModelView && (
            <FilterBar
              sortBy={sortBy}
              onSortChange={setSortBy}
              totalBrands={carBrands.length}
              totalModels={totalModels}
              totalDiscontinuedModels={totalDiscontinuedModels}
            />
          )}

          {/* Results */}
          {isModelView && searchResults.data.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-5xl sm:text-6xl mb-4">🔍</div>
              <p className="text-base sm:text-lg text-muted-foreground mb-2">
                No s'han trobat resultats
              </p>
              {searchQuery && (
                <p className="text-xs sm:text-sm text-muted-foreground/60">
                  Prova amb una altra cerca: "{searchQuery}"
                </p>
              )}
            </div>
          ) : isModelView ? (
            <>
              <p className="text-xs sm:text-sm text-muted-foreground mb-6 text-center font-light">
                {searchResults.data.length}{" "}
                {searchResults.data.length === 1 ? "model trobat" : "models trobats"}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                {(searchResults.data as Array<{ brand: string; brandId: string; model: any }>).map(
                  (result, index) => {
                    const modelImage = findModelImage(result.brandId, result.model.id, false);
                    const highResImage = getHighResImage(modelImage, 500);

                    return (
                      <div
                        key={`${result.brandId}-${result.model.id}-${index}`}
                        data-fuel={result.model.fuel || "none"}
                        className="relative bg-card border border-border rounded-2xl overflow-hidden hover:border-foreground hover:shadow-lg transition-all duration-300"
                      >
                        <div className="relative w-full h-48 bg-linear-to-br from-secondary to-muted flex items-center justify-center p-4">
                          {isSimilarModelImage(result.brandId, result.model.id) && (
                            <span className="absolute left-3 top-3 z-10 rounded-full bg-background/90 px-2.5 py-1 text-[10px] font-medium text-muted-foreground shadow-sm">
                              Model similar
                            </span>
                          )}
                          <img
                            src={highResImage}
                            alt={result.model.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                          />
                        </div>

                        <div className="p-6">
                          <div className="absolute top-4 left-4 z-10">
                            <Link
                              to={`/${result.brandId}`}
                              className="inline-flex items-center justify-center px-3 py-1.5 bg-foreground text-background rounded-full text-xs font-bold hover:opacity-80 transition-opacity shadow-md"
                            >
                              {result.brand}
                            </Link>
                          </div>

                          {result.model.fuel && (
                            <div className="absolute top-4 right-4 z-10">
                              <span className="inline-flex items-center justify-center px-3 py-1.5 bg-card/90 backdrop-blur-sm text-card-foreground rounded-full text-xs font-semibold shadow-sm">
                                {result.model.fuel}
                              </span>
                            </div>
                          )}

                          <div className="mb-4 pb-4 border-b border-border">
                            <h3 className="text-lg font-bold text-foreground">
                              {result.model.name}
                            </h3>
                          </div>

                          <div className="space-y-3 mb-4">
                            {result.model.power && (
                              <div className="flex items-start justify-between gap-3">
                                <span className="text-sm text-muted-foreground flex items-center gap-2 shrink-0">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
                                  </svg>
                                  Potència
                                </span>
                                <span className="text-sm font-semibold text-foreground text-right">
                                  {result.model.power} CV
                                </span>
                              </div>
                            )}

                            {result.model.trunk && (
                              <div className="flex items-start justify-between gap-3">
                                <span className="text-sm text-muted-foreground flex items-center gap-2 shrink-0">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect width="20" height="14" x="2" y="7" rx="2" ry="2" />
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                                  </svg>
                                  Maleter
                                </span>
                                <span className="text-sm font-semibold text-foreground text-right">
                                  {result.model.trunk} L
                                </span>
                              </div>
                            )}

                            {(result.model.length || result.model.width || result.model.height) && (
                              <div className="pt-2 border-t border-border">
                                <div className="flex items-center gap-2 mb-2">
                                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                                  </svg>
                                  <span className="text-xs text-muted-foreground">Dimensions</span>
                                </div>
                                <div className="text-xs text-muted-foreground font-mono bg-secondary px-2 py-1.5 rounded">
                                  {result.model.length && `${result.model.length}`}
                                  {result.model.width && ` × ${result.model.width}`}
                                  {result.model.height && ` × ${result.model.height}`} mm
                                </div>
                              </div>
                            )}
                          </div>

                          {result.model.price && (
                            <div className="pt-4 mt-4 border-t border-border">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-muted-foreground">Des de</span>
                                <span className="text-xl font-bold text-foreground">
                                  {result.model.price.includes("€")
                                    ? result.model.price
                                    : `${result.model.price}€`}
                                </span>
                              </div>
                            </div>
                          )}

                          <div className="mt-4 pt-3 border-t border-border flex gap-2">
                            <button
                              onClick={e => handleImageClick(result.brand, result.model.name, e)}
                              className="flex-1 px-4 py-2.5 bg-foreground text-background rounded-xl font-semibold text-sm hover:opacity-80 transition-opacity focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                              aria-label={`Veure imatges de ${result.model.name}`}
                            >
                              Imatges
                            </button>
                            <button
                              onClick={e => handleReviewClick(result.brand, result.model.name, e)}
                              className="flex-1 px-4 py-2.5 bg-card text-card-foreground border border-border rounded-xl font-semibold text-sm hover:bg-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background"
                              aria-label={`Veure ressenya de ${result.model.name}`}
                            >
                              Ressenya
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }
                )}
              </div>
            </>
          ) : filteredAndSortedBrands.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-5xl sm:text-6xl mb-4">🔍</div>
              <p className="text-base sm:text-lg text-muted-foreground mb-2">
                No s'han trobat resultats
              </p>
              <p className="text-xs sm:text-sm text-muted-foreground/60">
                Prova amb una altra cerca: "{searchQuery}"
              </p>
            </div>
          ) : (
            <>
              {searchQuery && (
                <p className="text-xs sm:text-sm text-muted-foreground mb-6 text-center font-light">
                  {filteredAndSortedBrands.length}{" "}
                  {filteredAndSortedBrands.length === 1 ? "marca trobada" : "marques trobades"}
                </p>
              )}
              {!isHomeReady && !searchQuery ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5 sm:gap-3">
                  {Array.from({ length: 18 }).map((_, i) => (
                    <BrandCardSkeleton key={i} />
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2.5 sm:gap-3">
                  {filteredAndSortedBrands.map(brand => (
                    <BrandCard key={brand.id} brand={brand} />
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

const ComparadorPage = () => {
  usePageMeta({
    title: "Comparador de models | Cotx.es",
    description:
      "Compara fins a 3 models de cotxe i descobreix les diferències en potència, preu, maleter i dimensions.",
  });

  return (
    <>
      <Comparador />
      <ScrollToTop />
    </>
  );
};

const LicensePlatesPage = () => {
  usePageMeta({
    title: "Guia: Matrícules | Cotx.es",
    description: "Aprèn a llegir els codis de matrícula i descobreix la història i curiositats del sistema.",
  });

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          <LicensePlates />
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

const LastLicensePlatePage = () => {
  usePageMeta({
    title: "Última matrícula | Cotx.es",
    description: "Consulta quina és l'última matrícula registrada.",
  });

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          <LastLicensePlate />
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

const CalculateLicensePlatePage = () => {
  usePageMeta({
    title: "Calculadora de matrícula | Cotx.es",
    description:
      "Calcula quina matrícula li correspondria al teu cotxe segons la data de matriculació.",
  });

  return (
    <>
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-12 max-w-7xl">
          <CalculateLicensePlate />
        </div>
      </div>
      <ScrollToTop />
    </>
  );
};

const CarListPage = () => {
  return <CarList />;
};

const GuiaModelsPage = () => {
  usePageMeta({
    title: "Guia: Models | Cotx.es",
    description: "Entén què volen dir els noms i les sigles dels models de cotxe, com GT, RS, AMG o TDI.",
  });

  return (
    <>
      <GuiaModels />
      <ScrollToTop />
    </>
  );
};

const TestSelectorPage = () => {
  usePageMeta({
    title: "Test: models | Cotx.es",
    description:
      "Posa a prova els teus coneixements sobre marques i models. Tria un mode i demostra el que saps.",
  });
  return (
    <>
      <TestSelector />
      <ScrollToTop />
    </>
  );
};

const TestMarquesPage = () => {
  usePageMeta({
    title: "Test: Endevina la marca | Cotx.es",
    description: "Endevina a quina marca pertany cada model de cotxe.",
  });
  return (
    <>
      <TestModels />
      <ScrollToTop />
    </>
  );
};

const TestDecodificaPage = () => {
  usePageMeta({
    title: "Test: Desxifra el nom | Cotx.es",
    description: "Descobreix què significa cada lletra i número dels noms dels models de cotxe.",
  });
  return (
    <>
      <TestDecodifica />
      <ScrollToTop />
    </>
  );
};

const TestPreuPage = () => {
  usePageMeta({
    title: "Endevina el preu | Cotx.es",
    description:
      "Mira la foto i les especificacions d'un cotxe i endevina'n el preu en 5 intents.",
  });
  return (
    <>
      <TestPreu />
      <ScrollToTop />
    </>
  );
};

export const App = () => {
  return (
    <BrowserRouter>
      <WelcomePopup />
      <PorscheCursor />
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/comparador" element={<ComparadorPage />} />
        <Route path="/preferits" element={<CarListPage />} />
        <Route path="/guia-matricules" element={<LicensePlatesPage />} />
        <Route path="/matricules" element={<LicensePlatesPage />} />
        <Route path="/ultima-matricula" element={<LastLicensePlatePage />} />
        <Route path="/guia-models" element={<GuiaModelsPage />} />
        <Route path="/guia-models/test" element={<TestSelectorPage />} />
        <Route path="/guia-models/test/marques" element={<TestMarquesPage />} />
        <Route path="/guia-models/test/desxifra" element={<TestDecodificaPage />} />
        <Route path="/endevina-preu" element={<TestPreuPage />} />
        <Route path="/calculadora-matricula" element={<CalculateLicensePlatePage />} />
        <Route path="/grups" element={<Grups />} />
        <Route path="/novetats" element={<Novetats />} />
        <Route path="/histories" element={<Histories />} />
        <Route path="/histories/porsche" element={<HistoriaPorsche />} />
        <Route path="/galeria" element={<Gallery />} />
        {/* Interna: sense enllaços ni sitemap, noindex. */}
        <Route path="/dades" element={<Dades />} />
        <Route path="/:brandId" element={<BrandDetailPage />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      <footer className="py-6 text-center text-sm text-muted-foreground">
        © 2026 Cotx.es // un projecte de{" "}
        <a
          href="https://f.cat"
          target="_blank"
          rel="noopener noreferrer"
          className="text-red-500 underline hover:text-red-600 transition-colors"
        >
          f.cat
        </a>
      </footer>
      <Analytics />
    </BrowserRouter>
  );
};

export default App;
